import React from 'react';
import styled from 'styled-components';
import { FaLightbulb, FaHandsHelping, FaShieldAlt, FaUsers, FaEye, FaBullseye } from 'react-icons/fa';
import { theme } from '../styles/theme';

const AboutContainer = styled.div`
  padding: 100px 0;
  background: ${theme.colors.primary};
`;

const AboutWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const AboutHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 48px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const AboutText = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
  margin-bottom: 35px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
`;

// Vision & Mission Section
const VisionMissionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 32px;
  margin-top: 64px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const VisionMissionCard = styled.div`
  background: ${theme.colors.primary};
  padding: 40px 30px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.secondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid ${theme.colors.tertiary};
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin: 16px 0;
  color: ${theme.colors.secondary};
`;

const CardIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.secondary};
  margin-bottom: 10px;
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const CardText = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
`;

// Values Section
const ValuesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 32px;
  margin-top: 64px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: ${theme.colors.primary};
  padding: 30px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.secondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid ${theme.colors.tertiary};
  }
`;

const ValueIcon = styled.div`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 16px;
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: ${theme.colors.secondary};
`;

const ValueText = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
`;

const About = () => {
  return (
    <AboutContainer id="about">
      <AboutWrapper>
        <AboutHeading>About Us</AboutHeading>
        <AboutText>
          Afrotech Group was founded by Bokang Mojela with a vision to build technology that speaks directly to the needs of Africa's people and institutions.
          From digital platforms to community tech programs, we aim to empower Africa through innovative technologies.
        </AboutText>
        
        <VisionMissionContainer>
          <VisionMissionCard>
            <CardIcon>
              <FaEye />
            </CardIcon>
            <CardTitle>Our Vision</CardTitle>
            <CardText>
              To become the leading African tech group driving digital empowerment, security, and convenience for all.
            </CardText>
          </VisionMissionCard>
          
          <VisionMissionCard>
            <CardIcon>
              <FaBullseye />
            </CardIcon>
            <CardTitle>Our Mission</CardTitle>
            <CardText>
              We develop user-focused tech solutions that solve real-world problems in asset management, service delivery, and communication.
            </CardText>
          </VisionMissionCard>
        </VisionMissionContainer>
        
        <AboutHeading style={{ marginTop: '80px' }}>Core Values</AboutHeading>
        <ValuesContainer>
          <ValueCard>
            <ValueIcon>
              <FaLightbulb />
            </ValueIcon>
            <ValueTitle>Innovation</ValueTitle>
            <ValueText>
              We embrace creative thinking and cutting-edge technology to develop solutions that address complex challenges.
            </ValueText>
          </ValueCard>
          
          <ValueCard>
            <ValueIcon>
              <FaHandsHelping />
            </ValueIcon>
            <ValueTitle>Empowerment</ValueTitle>
            <ValueText>
              We build technology that empowers individuals, communities, and organizations to achieve their full potential.
            </ValueText>
          </ValueCard>
          
          <ValueCard>
            <ValueIcon>
              <FaShieldAlt />
            </ValueIcon>
            <ValueTitle>Integrity</ValueTitle>
            <ValueText>
              We operate with honesty, transparency, and accountability in all our interactions and business practices.
            </ValueText>
          </ValueCard>
          
          <ValueCard>
            <ValueIcon>
              <FaUsers />
            </ValueIcon>
            <ValueTitle>Community</ValueTitle>
            <ValueText>
              We prioritize solutions that strengthen communities and foster collaboration across diverse stakeholders.
            </ValueText>
          </ValueCard>
        </ValuesContainer>
      </AboutWrapper>
    </AboutContainer>
  );
};

export default About;
